// SAD-494 (TEAM-02): workshare and agreement lifecycle for shortlisted or
// selected teaming candidates. An agreement is executed only with a linked
// document and an explicit attestation by the signed-in human; the server
// refuses anything else, so the button is never hidden behind client checks.
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { ConnectError } from '@connectrpc/connect';
import { SpecraBadge, SpecraButton, SpecraCheckboxField, SpecraEmptyState, SpecraOverlayDialog, SpecraSelect } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { projectDataClient } from '../../lib/connect/clients';
import { TextField } from './PastPerformance/Fields';
import { ReadOnlyScope } from './record/ReadOnlyScope';
const T = Testids.captureTeamingAgreements;
const label = (v)=>v.replace(/_/g, ' ');
const when = (d)=>d ? d.toDate().toISOString().slice(0, 10) : '';
const TERMINAL = new Set([
    'executed',
    'withdrawn'
]);
function AgreementRow({ agreement, onChange }) {
    const [note, setNote] = useState('');
    const [evidenceLabel, setEvidenceLabel] = useState('');
    const [evidenceRef, setEvidenceRef] = useState('');
    const [attest, setAttest] = useState(false);
    const [attestation, setAttestation] = useState('');
    const set = (state)=>onChange(async ({ requestId, captureProjectId })=>{
            await projectDataClient.setCaptureTeamingAgreementState({
                requestId,
                captureProjectId,
                agreementId: agreement.id,
                state,
                note,
                evidenceLabel,
                evidenceRef,
                attest,
                attestation
            });
            setNote('');
        });
    return /*#__PURE__*/ _jsxs("li", {
        "data-testid": T.agRow,
        className: "space-y-1 rounded-md border p-2 text-sm",
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "flex items-center gap-2",
                children: [
                    /*#__PURE__*/ _jsx("span", {
                        className: "font-medium",
                        children: agreement.agreementType.toUpperCase()
                    }),
                    /*#__PURE__*/ _jsx("span", {
                        "data-testid": T.agState,
                        children: /*#__PURE__*/ _jsx(SpecraBadge, {
                            children: label(agreement.state)
                        })
                    })
                ]
            }),
            /*#__PURE__*/ _jsx("ul", {
                className: "space-y-1 pl-3 text-xs",
                children: agreement.history.map((h)=>/*#__PURE__*/ _jsxs("li", {
                        "data-testid": T.agHistory,
                        children: [
                            label(h.state),
                            " on ",
                            when(h.at),
                            ": ",
                            h.note,
                            h.evidenceRef && ` — document: ${h.evidenceLabel} (${h.evidenceRef})`,
                            h.attestedBy && ` — attested by ${h.attestedBy}: ${h.attestation}`
                        ]
                    }, h.id))
            }),
            !TERMINAL.has(agreement.state) && /*#__PURE__*/ _jsxs("div", {
                className: "space-y-2",
                children: [
                    /*#__PURE__*/ _jsx(TextField, {
                        label: "State change note",
                        required: true,
                        testId: T.agStateNote,
                        value: note,
                        onChange: setNote
                    }),
                    /*#__PURE__*/ _jsx(TextField, {
                        label: "Executed document label",
                        testId: T.agEvidenceLabel,
                        value: evidenceLabel,
                        onChange: setEvidenceLabel
                    }),
                    /*#__PURE__*/ _jsx(TextField, {
                        label: "Executed document link",
                        testId: T.agEvidenceRef,
                        value: evidenceRef,
                        onChange: setEvidenceRef
                    }),
                    /*#__PURE__*/ _jsx(SpecraCheckboxField, {
                        label: "I am authorized to attest that this agreement has been executed.",
                        controlProps: {
                            ...{
                                'data-testid': T.agAttest
                            },
                            checked: attest,
                            onCheckedChange: (checked)=>setAttest(checked === true)
                        }
                    }),
                    /*#__PURE__*/ _jsx(TextField, {
                        label: "Attestation statement",
                        testId: T.agAttestation,
                        value: attestation,
                        onChange: setAttestation
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex gap-2",
                        children: [
                            agreement.state === 'draft' && /*#__PURE__*/ _jsx(SpecraButton, {
                                "data-testid": T.agNegotiate,
                                disabled: !note.trim(),
                                onClick: ()=>set('in_negotiation'),
                                children: "Move to negotiation"
                            }),
                            /*#__PURE__*/ _jsx(SpecraButton, {
                                "data-testid": T.agExecute,
                                disabled: !note.trim(),
                                onClick: ()=>set('executed'),
                                children: "Mark executed"
                            }),
                            /*#__PURE__*/ _jsx(SpecraButton, {
                                "data-testid": T.agWithdraw,
                                disabled: !note.trim(),
                                onClick: ()=>set('withdrawn'),
                                children: "Withdraw"
                            })
                        ]
                    })
                ]
            })
        ]
    });
}
function Partner({ name, candidateId, workshare, agreements, run, readOnly, error }) {
    const [ws, setWs] = useState({
        area: '',
        share: '',
        scope: '',
        notes: ''
    });
    const [type, setType] = useState('nda');
    const [note, setNote] = useState('');
    // GRW-39: both forms open from Add buttons; one dialog at a time.
    const [dialog, setDialog] = useState();
    const share = Number(ws.share);
    return /*#__PURE__*/ _jsxs("li", {
        "data-testid": T.partner,
        className: "space-y-2 rounded-md border p-2",
        children: [
            /*#__PURE__*/ _jsx("h4", {
                className: "font-semibold",
                children: name
            }),
            /*#__PURE__*/ _jsx("ul", {
                className: "space-y-1 text-sm",
                children: workshare.map((w)=>/*#__PURE__*/ _jsxs("li", {
                        "data-testid": T.wsRow,
                        children: [
                            w.area,
                            ": ",
                            w.plannedSharePct,
                            "%",
                            w.scope && ` — ${w.scope}`,
                            w.notes && ` — ${w.notes}`,
                            " (by ",
                            w.actorUserId,
                            " on ",
                            when(w.at),
                            ")"
                        ]
                    }, w.id))
            }),
            !readOnly && /*#__PURE__*/ _jsxs("div", {
                className: "flex flex-wrap gap-2",
                children: [
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        size: "sm",
                        onClick: ()=>setDialog('workshare'),
                        "data-testid": Testids.pursuitTeaming.workshareAddButton,
                        children: "Add workshare"
                    }),
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        size: "sm",
                        variant: "secondary",
                        onClick: ()=>setDialog('agreement'),
                        "data-testid": Testids.pursuitTeaming.agreementAddButton,
                        children: "Start agreement draft"
                    })
                ]
            }),
            /*#__PURE__*/ _jsx("ul", {
                className: "space-y-2",
                children: agreements.map((a)=>/*#__PURE__*/ _jsx(AgreementRow, {
                        agreement: a,
                        onChange: run
                    }, a.id))
            }),
            !readOnly && /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    /*#__PURE__*/ _jsx(SpecraOverlayDialog, {
                        open: dialog === 'workshare',
                        onOpenChange: (open)=>setDialog(open ? 'workshare' : undefined),
                        title: `Add workshare — ${name}`,
                        size: "lg",
                        testId: Testids.pursuitTeaming.workshareDialog,
                        footerProps: {
                            onCancel: ()=>setDialog(undefined),
                            confirmLabel: 'Add workshare',
                            onConfirm: ()=>run(async ({ requestId, captureProjectId })=>{
                                    await projectDataClient.addCaptureTeamingWorkshare({
                                        requestId,
                                        captureProjectId,
                                        candidateId,
                                        area: ws.area,
                                        plannedSharePct: share,
                                        scope: ws.scope,
                                        notes: ws.notes
                                    });
                                    setWs({
                                        area: '',
                                        share: '',
                                        scope: '',
                                        notes: ''
                                    });
                                    setDialog(undefined);
                                }),
                            confirmProps: {
                                disabled: !ws.area.trim() || ws.share.trim() === '' || !(share >= 0 && share <= 100),
                                'data-testid': T.wsAdd
                            }
                        },
                        children: /*#__PURE__*/ _jsxs("div", {
                            className: "space-y-3",
                            children: [
                                /*#__PURE__*/ _jsx(TextField, {
                                    label: "Workshare area",
                                    testId: T.wsArea,
                                    value: ws.area,
                                    onChange: (area)=>setWs((s)=>({
                                                ...s,
                                                area
                                            }))
                                }),
                                /*#__PURE__*/ _jsx(TextField, {
                                    label: "Planned share (%)",
                                    testId: T.wsShare,
                                    value: ws.share,
                                    onChange: (v)=>setWs((s)=>({
                                                ...s,
                                                share: v
                                            }))
                                }),
                                /*#__PURE__*/ _jsx(TextField, {
                                    label: "Scope",
                                    testId: T.wsScope,
                                    value: ws.scope,
                                    onChange: (scope)=>setWs((s)=>({
                                                ...s,
                                                scope
                                            }))
                                }),
                                /*#__PURE__*/ _jsx(TextField, {
                                    label: "Workshare notes",
                                    testId: T.wsNotes,
                                    value: ws.notes,
                                    onChange: (notes)=>setWs((s)=>({
                                                ...s,
                                                notes
                                            }))
                                }),
                                error && /*#__PURE__*/ _jsx("p", {
                                    role: "alert",
                                    children: error
                                })
                            ]
                        })
                    }),
                    /*#__PURE__*/ _jsx(SpecraOverlayDialog, {
                        open: dialog === 'agreement',
                        onOpenChange: (open)=>setDialog(open ? 'agreement' : undefined),
                        title: `Start agreement draft — ${name}`,
                        size: "md",
                        testId: Testids.pursuitTeaming.agreementDialog,
                        footerProps: {
                            onCancel: ()=>setDialog(undefined),
                            confirmLabel: 'Start agreement draft',
                            onConfirm: ()=>run(async ({ requestId, captureProjectId })=>{
                                    await projectDataClient.createCaptureTeamingAgreement({
                                        requestId,
                                        captureProjectId,
                                        candidateId,
                                        agreementType: type,
                                        note
                                    });
                                    setNote('');
                                    setDialog(undefined);
                                }),
                            confirmProps: {
                                disabled: !note.trim(),
                                'data-testid': T.agCreate
                            }
                        },
                        children: /*#__PURE__*/ _jsxs("div", {
                            className: "space-y-3",
                            children: [
                                /*#__PURE__*/ _jsxs(SpecraSelect, {
                                    "aria-label": "Agreement type",
                                    "data-testid": T.agType,
                                    className: "w-full rounded-md border bg-background p-2",
                                    value: type,
                                    onChange: (e)=>setType(e.target.value),
                                    children: [
                                        /*#__PURE__*/ _jsx("option", {
                                            value: "nda",
                                            children: "NDA"
                                        }),
                                        /*#__PURE__*/ _jsx("option", {
                                            value: "ta",
                                            children: "Teaming agreement"
                                        }),
                                        /*#__PURE__*/ _jsx("option", {
                                            value: "subcontract",
                                            children: "Subcontract"
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsx(TextField, {
                                    label: "Draft note",
                                    testId: T.agNote,
                                    value: note,
                                    onChange: setNote
                                }),
                                error && /*#__PURE__*/ _jsx("p", {
                                    role: "alert",
                                    children: error
                                })
                            ]
                        })
                    })
                ]
            })
        ]
    });
}
export function TeamingAgreements({ captureProjectId, readOnly = false }) {
    const queryClient = useQueryClient();
    const [error, setError] = useState('');
    const position = useQuery({
        queryKey: [
            'capture-teaming-position',
            captureProjectId
        ],
        queryFn: ()=>projectDataClient.getCaptureTeamingPosition({
                captureProjectId
            }),
        retry: false
    });
    const lifecycle = useQuery({
        queryKey: [
            'capture-teaming-agreements',
            captureProjectId
        ],
        queryFn: ()=>projectDataClient.getCaptureTeamingAgreements({
                captureProjectId
            }),
        retry: false
    });
    async function run(fn) {
        setError('');
        try {
            await fn({
                requestId: crypto.randomUUID(),
                captureProjectId
            });
            await queryClient.invalidateQueries({
                queryKey: [
                    'capture-teaming-agreements',
                    captureProjectId
                ]
            });
        } catch (e) {
            setError(ConnectError.from(e).rawMessage);
        }
    }
    if (lifecycle.isError) return /*#__PURE__*/ _jsx("p", {
        role: "alert",
        children: ConnectError.from(lifecycle.error).rawMessage
    });
    const partners = (position.data?.candidates ?? []).filter((c)=>c.status === 'shortlisted' || c.status === 'selected');
    return /*#__PURE__*/ _jsx(ReadOnlyScope, {
        readOnly: readOnly,
        children: /*#__PURE__*/ _jsxs("section", {
            "data-testid": T.root,
            className: "space-y-3 rounded-md border p-3",
            children: [
                /*#__PURE__*/ _jsx("h3", {
                    className: "font-semibold",
                    children: "Teaming workshare and agreements"
                }),
                /*#__PURE__*/ _jsx("p", {
                    className: "text-sm",
                    children: "Tracked for shortlisted or selected partners. An agreement counts as executed only when a person links the executed document and attests to it; completing a task or accepting a suggestion never executes one."
                }),
                partners.length === 0 && /*#__PURE__*/ _jsx(SpecraEmptyState, {
                    size: "compact",
                    title: "No shortlisted or selected partners yet.",
                    description: "Shortlist a teaming candidate to track workshare and agreements.",
                    "data-testid": Testids.pursuitTeaming.agreementsEmptyState
                }),
                /*#__PURE__*/ _jsx("ul", {
                    className: "space-y-2",
                    children: partners.map((c)=>/*#__PURE__*/ _jsx(Partner, {
                            name: c.companyName || c.companyProfileId,
                            candidateId: c.id,
                            workshare: (lifecycle.data?.workshare ?? []).filter((w)=>w.candidateId === c.id),
                            agreements: (lifecycle.data?.agreements ?? []).filter((a)=>a.candidateId === c.id),
                            run: run,
                            readOnly: readOnly,
                            error: error
                        }, c.id))
                }),
                error && /*#__PURE__*/ _jsx("p", {
                    role: "alert",
                    "data-testid": T.error,
                    children: error
                })
            ]
        })
    });
}
